import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import Icon from '../../../components/AppIcon';
import { articlesService, projectsService, subUnionsService, citiesService } from '../../../services/dataService';

const ChartsSection = () => {
  const [articleTrendsData, setArticleTrendsData] = useState([]);
  const [projectStatusData, setProjectStatusData] = useState([]);
  const [subUnionDistributionData, setSubUnionDistributionData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Arabic month names
  const monthNames = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch all data
        const [articles, projects, subUnions, cities] = await Promise.all([
          articlesService.getAll().catch(() => []),
          projectsService.getAll().catch(() => []),
          subUnionsService.getAll().catch(() => []),
          citiesService.getAll().catch(() => [])
        ]);

        // Calculate article trends by month (last 6 months)
        const now = new Date();
        const last6Months = [];
        for (let i = 5; i >= 0; i--) {
          const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
          last6Months.push({
            month: monthNames[date.getMonth()],
            monthIndex: date.getMonth(),
            year: date.getFullYear()
          });
        }

        const articleTrends = last6Months.map(({ month, monthIndex, year }) => {
          const monthArticles = articles.filter(article => {
            const articleDate = new Date(article.date || article.createdAt);
            return articleDate.getMonth() === monthIndex && articleDate.getFullYear() === year;
          });
          
          const publishedArticles = monthArticles.filter(a => a.published);
          const eventArticles = monthArticles.filter(a => a.category === 'events' && a.published);
          
          return {
            month,
            articles: publishedArticles.length,
            events: eventArticles.length
          };
        });
        setArticleTrendsData(articleTrends);

        // Calculate project status distribution
        const completed = projects.filter(p => p.status === 'completed').length;
        const ongoing = projects.filter(p => p.status === 'ongoing').length;
        const planned = projects.filter(p => p.status === 'planned').length;
        const total = projects.length || 1;

        const projectStatus = [
          { name: 'مكتملة', value: Math.round((completed / total) * 100), count: completed, color: '#059669' },
          { name: 'قيد التنفيذ', value: Math.round((ongoing / total) * 100), count: ongoing, color: '#dcb557' },
          { name: 'مخططة', value: Math.round((planned / total) * 100), count: planned, color: '#2563eb' }
        ].filter(item => item.count > 0);
        setProjectStatusData(projectStatus);

        // Calculate sub-union distribution by city
        const cityDistribution = cities.map(city => {
          const count = subUnions.filter(union => union.cityId === city.id).length;
          return {
            city: city.nameAr || city.nameEn || 'Unknown',
            count
          };
        }).filter(item => item.count > 0).slice(0, 5); // Top 5 cities
        setSubUnionDistributionData(cityDistribution);

      } catch (err) {
        console.error('Error fetching chart data:', err);
        // Set empty data on error
        setArticleTrendsData([]);
        setProjectStatusData([]);
        setSubUnionDistributionData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchChartData();
  }, []);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevation-2">
          <p className="text-sm font-medium text-popover-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {entry?.dataKey}: {entry?.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
      {/* Article Publication Trends */}
      <div className="lg:col-span-2 bg-card border border-border rounded-lg shadow-elevation-1">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground font-arabic">اتجاهات النشر</h3>
            <Icon name="TrendingUp" size={20} className="text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground mt-1 font-caption">المقالات والفعاليات المنشورة شهرياً</p>
        </div>
        <div className="p-6">
          {isLoading ? (
            <div className="w-full h-64 flex items-center justify-center">
              <div className="animate-pulse text-muted-foreground">جاري تحميل البيانات...</div>
            </div>
          ) : articleTrendsData.length > 0 ? (
            <div className="w-full h-64" aria-label="Article Publication Trends Chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={articleTrendsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="month" 
                    stroke="#6b7280"
                    fontSize={12}
                  />
                  <YAxis stroke="#6b7280" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="articles" fill="#1f4333" name="المقالات" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="events" fill="#dcb557" name="الفعاليات" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="w-full h-64 flex items-center justify-center text-muted-foreground">
              لا توجد بيانات متاحة
            </div>
          )}
        </div>
      </div>
      {/* Project Status Distribution */}
      <div className="bg-card border border-border rounded-lg shadow-elevation-1">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground font-arabic">حالة المشاريع</h3>
            <Icon name="PieChart" size={20} className="text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground mt-1 font-caption">توزيع المشاريع حسب الحالة</p>
        </div>
        <div className="p-6">
          {isLoading ? (
            <div className="w-full h-64 flex items-center justify-center">
              <div className="animate-pulse text-muted-foreground">جاري تحميل البيانات...</div>
            </div>
          ) : projectStatusData.length > 0 ? (
            <div className="w-full h-64" aria-label="Project Status Distribution Chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={projectStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {projectStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry?.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="w-full h-64 flex items-center justify-center text-muted-foreground">
              لا توجد بيانات متاحة
            </div>
          )}
          <div className="mt-4 space-y-2">
            {projectStatusData?.length > 0 ? (
              projectStatusData.map((item, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: item?.color }}
                    />
                    <span className="text-foreground">{item?.name}</span>
                  </div>
                  <span className="text-muted-foreground">{item?.value}%</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center">لا توجد بيانات</p>
            )}
          </div>
        </div>
      </div>
      {/* Sub-Union Distribution */}
      <div className="lg:col-span-2 xl:col-span-1 bg-card border border-border rounded-lg shadow-elevation-1">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground font-arabic">توزيع الاتحادات</h3>
            <Icon name="Building2" size={20} className="text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground mt-1 font-caption">الاتحادات الفرعية حسب المدينة</p>
        </div>
        <div className="p-6">
          {isLoading ? (
            <div className="w-full h-64 flex items-center justify-center">
              <div className="animate-pulse text-muted-foreground">جاري تحميل البيانات...</div>
            </div>
          ) : subUnionDistributionData.length > 0 ? (
            <div className="w-full h-64" aria-label="Sub-Union Distribution Chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={subUnionDistributionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="city" 
                    stroke="#6b7280"
                    fontSize={12}
                  />
                  <YAxis stroke="#6b7280" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#1f4333" 
                    strokeWidth={3}
                    dot={{ fill: '#1f4333', strokeWidth: 2, r: 6 }}
                    activeDot={{ r: 8, stroke: '#1f4333', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="w-full h-64 flex items-center justify-center text-muted-foreground">
              لا توجد بيانات متاحة
            </div>
          )}
        </div>
      </div>
      {/* Performance Summary */}
      <div className="xl:col-span-2 bg-card border border-border rounded-lg shadow-elevation-1">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground font-arabic">ملخص الأداء</h3>
            <Icon name="Activity" size={20} className="text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground mt-1 font-caption">مؤشرات الأداء الرئيسية لهذا الشهر</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-success/10 text-success rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name="Users" size={24} />
              </div>
              <p className="text-2xl font-bold text-foreground">1,247</p>
              <p className="text-sm text-muted-foreground font-caption">عضو نشط</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name="FileText" size={24} />
              </div>
              <p className="text-2xl font-bold text-foreground">89</p>
              <p className="text-sm text-muted-foreground font-caption">مقال منشور</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-warning/10 text-warning rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name="Calendar" size={24} />
              </div>
              <p className="text-2xl font-bold text-foreground">24</p>
              <p className="text-sm text-muted-foreground font-caption">فعالية مخططة</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-secondary/10 text-secondary rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name="Award" size={24} />
              </div>
              <p className="text-2xl font-bold text-foreground">12</p>
              <p className="text-sm text-muted-foreground font-caption">مشروع مكتمل</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartsSection;